//#region Libraries
import { useState } from "react";
//#endregion
//#region Styles
import { TitleSection } from "components/styles";
import { Container, Form } from "../styles";
//#endregion
//#region Components
import DialogsActionsResource from "../../Dialogs/DialogsActionsResource/DialogsActionsResource";
import Fields from "./Fields/Fields";
import Footer from "./Footer/Footer";
//#endregion
//#region Services
import { 
    requestDeleteResource, 
    requestToggleActivationResource 
} from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import { FORM_ACTION_ERRORS, RESOURCE_TEXT_LIST } from "views/Recursos/ResourcesLibrary/utils/constants";
import useDialogShowActionResource from "../utils/hooks/useDialogShowActionResource";
import DialogCannotBeDoneAction from "../../Dialogs/DialogCannotBeDoneAction/DialogCannotBeDoneAction";
import useNameForCreatingResource from "../utils/hooks/useNameForCreatingResource";
import useElectronicResourceForm from "./utils/hooks/useElectronicResourceForm";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import DialogErrorForFutureReservationsInDesactivation from "../../Dialogs/DialogErrorForFutureReservationsInDesactivation";
import DialogErrorForFutureReservationsInDeletion from "../../Dialogs/DialogErrorForFutureReservationsInDeletion";
//#endregion

const ElectronicResourceForm = ({
    isAdmin,
    campusFilterSelectedId,
    resourceTypeIdToCreate,     
    currentElectronicResourceForUpdate,
    data: {
        electronicBrands,
        libraryList,
        campusList,
        resourceTypes
    },
    show,
    toggleShow, 
    loadingRequest, 
    fillServiceList, 
    filtersAreCompleted
}) => {
    const isCreate = !currentElectronicResourceForUpdate;
    //#region Extra
    const { dialogShowDeletionResource,  dialogShowToggleActivationResource } = useDialogShowActionResource();
    const dialogErrorForFutureReservationsInDesactivation = useShowDialogAlert();
    const dialogErrorForFutureReservationsInDeletion  = useShowDialogAlert();
    //#endregion
    //#region States
    const form = useElectronicResourceForm({
        campusFilterSelectedId,
        currentResourceForUpdate: currentElectronicResourceForUpdate,
        resourceTypeIdToCreate
    });
    const [electronicBrandsByResource, setElectronicBrandsByResource] = useState([]);
    const [dialogCannotBeDone, setDialogCannotBeDone] = useState({
        show: false,
        action: ""
    });
    //#region Extra
    const currentTextResource = RESOURCE_TEXT_LIST[form.value.resourceTypeId];
    //#endregion
    //#region Effects
    useNameForCreatingResource({
        form,
        isCreate,
        loadingRequest,
        doActionBeforeFillName: () => {
            setElectronicBrandsByResource(electronicBrands.filter(brand => brand.resourceTypeId === form.value.resourceTypeId));
        }
    });
    //#endregion
    //#region Functions
    const toggleActivationResource = async textList => {
        loadingRequest.show(`${textList.loading} ${currentTextResource.minus.singular}...`);
        // Realizando petición de activación o desactivación
        const { success, message } = await requestToggleActivationResource({
            resourceId: form.value.id,
            active: form.value.active
        });
        // Gestionando respuesta
        if (success) {
            // Exitosa
            closeFormDialog();
            fillServiceList({
                messageForToast: `¡${textList.success} con éxito la ${form.value.name}!`
            });
            return;
        }
        cancelToggleActivationResource();
        // Fallida
        loadingRequest.hide();
        switch (message) {
            case FORM_ACTION_ERRORS.FUTURE_RESERVATIONS:
                dialogErrorForFutureReservationsInDesactivation.open();
                return;
            default:
                setDialogCannotBeDone({
                    show: true,
                    action: "desactivar"
                });
        }
    }
    const deleteResource = async reasonForDeletion => {
        loadingRequest.show(`Eliminando ${currentTextResource.minus.singular}...`);
        const { success, message } = await requestDeleteResource({
            resourceId: form.value.id,
            reasonForDeletion
        });
        if (success) {
            // Exitosa
            closeFormDialog();
            fillServiceList({
                messageForToast: `¡El servicio ${form.value.name} fue eliminado existosamente!`
            });
            return;
        }
        // Fallida
        loadingRequest.hide();
        switch (message) {
            case FORM_ACTION_ERRORS.FUTURE_RESERVATIONS:
                dialogErrorForFutureReservationsInDeletion.open();
                return;
            default:
                setDialogCannotBeDone({
                    show: true,
                    action: "eliminar"
                });
        }
    }
    const cancelToggleActivationResource = () => {
        form.toggleActiveField();
        dialogShowToggleActivationResource.toggle(false);
    }
    const closeFormDialog = () => toggleShow(false);
    const toggleShowDialogCannotBeDone = () => {
        setDialogCannotBeDone(prev => ({
            ...prev,
            show: !prev.show
        }));
    }
    //#endregion
    if (!form.value.resourceTypeId || !currentTextResource) return null;
    return (
        <>
        <Container>
            <TitleSection fontWeight="900">
                {isCreate ? "Registrar" : "Editar"} Recurso
            </TitleSection>
            <Form onSubmit={e => e.preventDefault()}>
                <Fields 
                    isAdmin={isAdmin}
                    setCurrentResourceTypeIdToForm={resourceTypeIdToCreate.set}
                    currentElectronicResourceForUpdate={currentElectronicResourceForUpdate}
                    currentTextService={currentTextResource}
                    toggleShowDialogToggleActivationResource={dialogShowToggleActivationResource.toggle}
                    data={{
                        brands: electronicBrandsByResource,
                        libraryList,
                        campusList,
                        resourceTypes
                    }}
                    form={form}
                    isCreate={isCreate}
                    loadingRequest={loadingRequest}/>
                <Footer 
                    isAdmin={isAdmin}
                    closeFormDialog={closeFormDialog}
                    toggleDialogShowDeletionResource={dialogShowDeletionResource.toggle}
                    form={form}
                    getBrandName={brandId => electronicBrands.find(({ id }) => id === brandId)?.name || ""}
                    loadingRequest={loadingRequest}
                    filtersAreCompleted={filtersAreCompleted}
                    fillServiceList={fillServiceList}
                    isCreate={isCreate}
                    currentElectronicResourceForUpdate={currentElectronicResourceForUpdate}/>
            </Form>
        </Container>
        {(!isCreate && show) && 
        <>
        <DialogsActionsResource 
            isResourceActive={form.value.active}
            showDialogsActionResource={{
                dialogShowDeletionResource,
                dialogShowToggleActivationResource
            }}
            actions={{
                cancelToggleActivationResource,
                toggleActivationResource,
                deleteResource
            }}
            data={{
                serviceName: form.value.name,
                serviceTypeName: currentTextResource.minus.singular
            }}/>
        <DialogCannotBeDoneAction 
            action={dialogCannotBeDone.action}
            resourceName={form.value.name} 
            show={dialogCannotBeDone.show} 
            toggleShow={toggleShowDialogCannotBeDone}/>
        <DialogErrorForFutureReservationsInDesactivation
            dialog={dialogErrorForFutureReservationsInDesactivation}/>
        <DialogErrorForFutureReservationsInDeletion
            dialog={dialogErrorForFutureReservationsInDeletion}/>
        </>}
        </>
    );
}

export default ElectronicResourceForm;